// Selling on the web: prices, sale terms, pack drafts and payout copy.
// Pure functions, shared by License studio, Make pack and Payment settings,
// and tested in tests/selling.test.mjs.
//
// Sale terms port cloudloop-flask app/pricing.py sale_terms (decision 15):
// rights reserved with no price is NOT for sale, never free; a sale is name
// your price (a minimum plus an optional suggested price); a rights-reserved
// sound without a price that sits in a pack is a bonus sound that comes with
// buying the pack.

export const OPEN_LICENSES = new Set([
  "CC0",
  "CC-BY",
  "CC-BY-NC",
  "CC-BY-SA",
  "CC-BY-NC-SA",
  "CC-BY-ND",
  "CC-BY-NC-ND",
]);
export const RIGHTS_RESERVED = "rights-reserved";
// The smallest charge PayPal takes: the floor of a checkout whose $0
// minimum the creator did not explicitly choose.
const MIN_CHARGE_USD = 0.01;

// The licences License studio offers, short enough for a pop-up button
// (no-derivatives ones are no longer applied, so they are not offered).
// Every CC licence is free to use; rights reserved sells licenses.
export const LICENSE_OPTIONS = [
  { label: "CC0: public domain", value: "CC0" },
  { label: "CC-BY: attribution", value: "CC-BY" },
  { label: "CC-BY-NC: non-commercial", value: "CC-BY-NC" },
  { label: "CC-BY-SA: share-alike", value: "CC-BY-SA" },
  { label: "Rights reserved", value: RIGHTS_RESERVED },
];

const money = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? Math.round(n * 100) / 100 : null;
};

// "$2.00". Two decimals always, so prices line up in a column.
export function usd(n) {
  const v = money(n) ?? 0;
  return `$${v.toFixed(2)}`;
}

// A typed price: "$2.98", "2.98", " 3 " -> number; "" or junk -> null.
export function parseUsd(text) {
  const s = String(text ?? "")
    .trim()
    .replace(/^\$\s*/, "");
  if (!s || !/^\d*(\.\d*)?$/.test(s) || s === ".") return null;
  return money(s);
}

// pricing.sale_terms on a loop_metadata (or merged loop) object.
export function saleTerms(md) {
  const m = md || {};
  const lt = m.license_type || "CC0";
  const out = {
    license_type: lt,
    license_state: "custom",
    for_sale: false,
    is_free: false,
    min_usd: null,
    suggested_usd: null,
    checkout_min_usd: null,
  };
  if (OPEN_LICENSES.has(lt)) return { ...out, license_state: "open", is_free: true };
  if (lt !== RIGHTS_RESERVED) return out;
  const min = Math.max(money(m.min_price_usd) || 0, 0);
  let sug = money(m.suggested_price_usd);
  if (sug !== null && sug <= 0) sug = null;
  let floor;
  if (m.for_sale === true) floor = min;
  else if (min > 0 || sug !== null) floor = Math.max(min, MIN_CHARGE_USD);
  else return { ...out, license_state: "not_for_sale" };
  if (sug !== null && sug < floor) sug = floor;
  return {
    ...out,
    license_state: "for_sale",
    for_sale: true,
    min_usd: min,
    suggested_usd: sug,
    checkout_min_usd: floor,
  };
}

// What a sound's price chip says (the app's LicenseChip): "Free" for an
// open licence, the checkout minimum when it is for sale, "Not for sale"
// for rights reserved with no price, or "Bonus" for that same sound inside
// a pack. kind picks the chip's colours.
export function priceChip(md, { inPack = false } = {}) {
  const t = saleTerms(md);
  if (t.is_free) return { kind: "free", label: "Free" };
  if (t.for_sale && (!inPack || t.checkout_min_usd > 0)) {
    return { kind: "price", label: usd(t.checkout_min_usd) };
  }
  if (inPack) return { kind: "bonus", label: "Bonus" };
  if (t.license_state === "custom") return { kind: "muted", label: "Custom" };
  return { kind: "muted", label: "Not for sale" };
}

// The platform fee line, from payment-settings' platform_fee_pct.
export function feeLine(pct) {
  const p = Number(pct) || 0;
  if (p <= 0) return "No platform fee right now.";
  const shown = Number.isInteger(p) ? String(p) : p.toFixed(1);
  return `cloudloop keeps ${shown}% of each sale.`;
}

// A sound's name on screen: its own name, else the file name, else the
// app's "Loop 4f2a1c".
export function soundName(loop) {
  const l = loop || {};
  const md = l.loop_metadata || {};
  if (l.name) return l.name;
  if (md.name) return md.name;
  if (md.filename) return md.filename.replace(/\.[^/.]+$/, "");
  const h = l.hash || md.hash || "";
  return h ? `Loop ${h.replace(/-/g, "").slice(0, 6)}` : "Untitled loop";
}

// A title the server made up for an unnamed sound ("Loop eb8db5c1": GET
// /sounds rows, /purchases/library?detail=1 and /sales?detail=1 take the
// hash's first 8 characters) rather than a name someone gave it.
export function isHashTitle(title, hash) {
  const m = /^loop ([0-9a-f-]+)$/i.exec(String(title || "").trim());
  if (!m) return false;
  const h = String(hash || "").replace(/-/g, "").toLowerCase();
  const t = m[1].replace(/-/g, "").toLowerCase();
  return !!h && t.length >= 6 && h.startsWith(t);
}

// A sound's name from a server row's title: the title, unless it is the
// server's "Loop eb8db5c1", which reads "Loop eb8db5" here as on every
// other page (soundName).
export function titledSoundName(title, hash) {
  const t = String(title || "").trim();
  return t && !isHashTitle(t, hash) ? t : soundName({ hash });
}

// 8.27 s -> "0:08".
export function clock(seconds) {
  const s = Math.max(0, Math.round(Number(seconds) || 0));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

// What a pack made from a session holds (decision 12: your own sounds
// only). sounds: [{ hash, creator, md }]. priced sounds set the pack's
// starting price; bonus sounds come only with the pack.
export function packDraft(sounds, me) {
  const own = (sounds || []).filter((s) => s.creator === me);
  const foreign = (sounds || []).filter((s) => s.creator !== me);
  const priced = [];
  const bonus = [];
  for (const s of own) {
    const t = saleTerms(s.md);
    if (t.is_free) continue;
    if (t.for_sale && t.checkout_min_usd > 0) priced.push(s);
    else bonus.push(s);
  }
  const total = priced.reduce((sum, s) => sum + saleTerms(s.md).checkout_min_usd, 0);
  return { own, foreign, priced, bonus, pricedTotal: Math.round(total * 100) / 100 };
}

// Make pack's line beside the pack price.
export function packPriceRule(draft) {
  if (draft.priced.length) return "Starts at the total of the paid sounds";
  if (draft.bonus.length) return "Choose a price. $0 gives the bonus sounds away";
  return "Every sound is free, so the pack is too";
}

// The confirmation before a $0 pack gives bonus sounds away (decision 15).
export function bonusGiveawayLine(n) {
  return n === 1
    ? "This sound will be free to anyone who gets the pack."
    : `These ${n} sounds will be free to anyone who gets the pack.`;
}

// A pack title as the art's two lines ("Friday Jam Pack" -> "Friday",
// "Jam"), as the app's packArtLines: a trailing "Pack" or "Vol. N" goes
// first, a long single word breaks with a hyphen, a long last word is cut.
export function packArtLines(title) {
  const raw = String(title || "").trim();
  let t = raw.replace(/\s+(Pack|Vol\.?\s*\d+)$/i, "").trim();
  if (!t) t = raw;
  const fit = 9;
  const words = t.split(/\s+/).filter(Boolean);
  if (words.length <= 1) {
    const w = words[0] || "";
    if (w.length <= fit) return [w, null];
    const cut = Math.floor(w.length * 0.55);
    return [`${w.slice(0, cut)}-`, w.slice(cut)];
  }
  const half = Math.ceil(words.length / 2);
  const first = words.slice(0, half).join(" ");
  let second = words.slice(half).join(" ");
  if (words.length === 2 && second.length > fit) second = second.slice(0, 4);
  return [first, second];
}

// A title as a slug ("Porch Tapes" -> "porch-tapes"), the way the server
// starts pack slugs and the app's slugOf.
export function slugOf(title) {
  return String(title || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

// How many looks "Change art…" steps through before it comes back round.
export const PACK_ART_STEPS = 6;

// Make pack's art seed: the name's slug, stepped by "Change art…"
// ("porch-tapes", "porch-tapes~1").
export function packArtSeedFor(title, step = 0) {
  const slug = slugOf(title) || "new-pack";
  return step ? `${slug}~${step}` : slug;
}

// The artwork Make pack publishes, as the app's make_pack_sheet does:
// nothing until "Change art…" has stepped the art (pack views then draw
// the slug's tile), else "art:<seed>", which is never a picture URL.
export function packArtworkFor(title, step = 0) {
  return step ? `art:${packArtSeedFor(title, step)}` : null;
}

// The payout line under the pack price (MacMakePack's payout note). Sounds by other people never go in (decision
// 12); the sheet's list shows what does, so the note doesn't count them.
export function packPayoutLine(feePct) {
  return `Paid out to your PayPal. ${feeLine(feePct)} Packs hold your own sounds for now.`;
}

// A price field's text: two decimals ("2.00"), or "" for no price.
export function priceText(v) {
  const n = money(v);
  return n == null ? "" : n.toFixed(2);
}

// ------------------------------------------------------------- Set price
// The app's set_price_sheet.dart (SI-17): four choices through
// PUT /sounds/<h>/pricing. CC BY-NC and CC BY-SA can no longer be chosen;
// a sound already under one keeps it until a new choice is saved.

export const PRICING_CHOICES = [
  { value: "cc0", title: "Free (CC0)", body: "Anyone can use it for anything. Can't be undone." },
  { value: "cc_by", title: "Free with credit (CC BY)", body: "Anyone can use it if they credit you." },
  { value: "for_sale", title: "For sale", body: "Name your price: a minimum, and where buyers start." },
  { value: "not_for_sale", title: "Not for sale", body: "Rights reserved: no download, no license." },
];

// "CC-BY-NC" -> "CC BY-NC", as the sheet names a licence.
export function licenseName(licenseType) {
  const lt = String(licenseType || "CC0").trim();
  if (lt === RIGHTS_RESERVED) return "Rights reserved";
  return lt.replace(/^CC-/i, "CC ");
}

// The choice a Sound (kit/sound.js) shows now, or null when its licence is
// one the sheet no longer offers (CC BY-NC, CC BY-SA, …).
export function pricingChoiceOf(sound) {
  const lt = String((sound && sound.licenseType) || "CC0").toUpperCase();
  if (lt === "CC0") return "cc0";
  if (lt === "CC-BY") return "cc_by";
  if (OPEN_LICENSES.has(lt) || lt.startsWith("CC")) return null;
  if (sound && sound.forSale) return "for_sale";
  return "not_for_sale";
}

// What the sheet starts on for one or more sounds: their shared choice, or
// none when they differ (as License studio's bulk editor never preselected
// one: CC0 can't be undone).
export function initialChoice(sounds) {
  const list = (sounds || []).filter(Boolean);
  if (!list.length) return null;
  const first = pricingChoiceOf(list[0]);
  return list.every((s) => pricingChoiceOf(s) === first) ? first : null;
}

// "Now CC BY-NC. Pick a license to change it." for a licence the sheet no
// longer offers, else "".
export function keptLicenseNote(sounds) {
  const odd = (sounds || []).find((s) => s && pricingChoiceOf(s) === null);
  return odd ? `Now ${licenseName(odd.licenseType)}. Pick a license to change it.` : "";
}

// Every sound is already CC0: nothing else can be chosen.
export function cc0Locked(sounds) {
  const list = (sounds || []).filter(Boolean);
  return list.length > 0 && list.every((s) => String(s.licenseType || "").toUpperCase() === "CC0");
}

// The minimum and suggested fields' first text (the app's 1.00 and 2.00
// when the sound has no price yet).
export function priceFieldsFor(sound) {
  const p = (sound && sound.price) || null;
  const min = p && p.minUsd != null ? Number(p.minUsd) : 1;
  const sug = p && p.suggestedUsd != null ? Number(p.suggestedUsd) : p && p.minUsd != null ? Number(p.minUsd) : 2;
  return { min: min.toFixed(2), suggested: sug.toFixed(2) };
}

// Checks the typed prices: { min, suggested, error }.
export function checkPrices(minText, suggestedText) {
  const min = parseUsd(minText);
  const suggested = String(suggestedText ?? "").trim() === "" ? null : parseUsd(suggestedText);
  if (min === null) return { min, suggested, error: "Enter a minimum ($0 is allowed)." };
  if (String(suggestedText ?? "").trim() !== "" && suggested === null) {
    return { min, suggested, error: "Enter a suggested price, or leave it empty." };
  }
  if (suggested !== null && suggested < min) {
    return { min, suggested, error: "The suggested price can't be under the minimum." };
  }
  return { min, suggested, error: null };
}

// The line under the price fields.
export function priceHint(minText) {
  return parseUsd(minText) === 0
    ? "A $0 minimum lets anyone get it free; buyers can still pay."
    : "Buyers pay at least the minimum and start at the suggested price.";
}

// PUT /sounds/<h>/pricing's body.
export function pricingBody(choice, { min = null, suggested = null, confirmCc0 = false } = {}) {
  const body = { license: choice };
  if (choice === "for_sale") {
    body.min_usd = min ?? 0;
    if (suggested != null) body.suggested_usd = suggested;
  }
  if (confirmCc0) body.confirm_cc0 = true;
  return body;
}

// The confirmation before releasing sounds as CC0, which can never be
// undone (the server's 409 cc0_irreversible): a ClAlert. It replaces
// License studio's cc0ConfirmText, which went with that page.
export function cc0Confirm(n = 1) {
  const one = n === 1;
  return {
    title: "CC0 can't be undone",
    body: `Anyone may use ${one ? "this sound" : "these sounds"} for anything, for good. You can never sell or reserve ${one ? "it" : "them"} again.`,
    confirmLabel: "Release as CC0",
  };
}

// 1073741824 -> "1.0 GB", 524288000 -> "500 MB".
function gbText(bytes) {
  const b = Number(bytes) || 0;
  return b >= 1 << 30 ? `${(b / (1 << 30)).toFixed(1)} GB` : `${Math.round(b / (1 << 20))} MB`;
}

// A 413 storage_quota_exceeded, worded.
export function quotaMessage(q) {
  const d = q || {};
  return `Your storage is full: ${gbText(d.used_bytes)} of ${gbText(d.quota_bytes)} on the ${d.tier || "free"} plan. Sounds you sell or keep rights reserved count toward storage; free (CC) sounds don't.`;
}

// A refused Set price, worded: { message, payout, priceError }. `err` is
// the API client's {status, message, body}; the server's code is at
// body.data.results.error.
export function pricingRefusal(err) {
  const e = err || {};
  const details = (e.body && e.body.data && e.body.data.results) || {};
  const code = details.error || "";
  const out = { message: "", payout: false, priceError: null, code };
  if (code === "cc0_permanent") out.message = "This sound was released as CC0, which can't be undone.";
  else if (code === "payout_required" || (!code && e.status === 412)) {
    out.message = "Link a PayPal payout method before selling sounds.";
    out.payout = true;
  } else if (code === "storage_quota_exceeded" || (!code && e.status === 413)) {
    out.message = code ? quotaMessage(details) : "Your storage is full.";
  } else if (code === "invalid_price") {
    out.message = e.message || "Check the prices.";
    out.priceError = out.message;
  } else if (code === "not_owner" || (!code && e.status === 403)) {
    out.message = "Only the person who made a sound can price it.";
  } else if (e.status === 404 || e.status === 405) {
    out.message = "Setting prices here is coming soon.";
  } else {
    out.message = `Couldn't set the price. ${e.message || "Try again."}`.trim();
  }
  return out;
}

// A bulk save's toast: "2 saved, 1 couldn't: Shaker pad (Your storage is
// full: …)". results: [{ title, ok, message }].
export function bulkSummary(results) {
  const list = results || [];
  const ok = list.filter((r) => r.ok).length;
  const bad = list.filter((r) => !r.ok);
  if (!bad.length) return `${ok} saved`;
  const why = bad.map((r) => `${r.title} (${String(r.message || "").replace(/\.$/, "")})`).join(", ");
  return `${ok} saved, ${bad.length} couldn't: ${why}`;
}

// ---------------------------------------------------------- Edit details

// The roots Edit details offers (the app's kSoundKeyRoots), as the wire
// spells them and as they read.
export const KEY_ROOTS = [
  ["C", "C"],
  ["C#", "C♯"],
  ["D", "D"],
  ["Eb", "E♭"],
  ["E", "E"],
  ["F", "F"],
  ["F#", "F♯"],
  ["G", "G"],
  ["Ab", "A♭"],
  ["A", "A"],
  ["Bb", "B♭"],
  ["B", "B"],
];

// "F♯m" or "F#m" -> "F#m" (the wire's spelling); "" -> null.
export function wireKey(key) {
  const k = String(key || "").trim();
  if (!k) return null;
  return k.replace(/♯/g, "#").replace(/♭/g, "b");
}

// "F#m" -> "F♯m"; null -> "No key".
export function keyText(key) {
  const k = wireKey(key);
  if (!k) return "No key";
  return k.replace(/^([A-G])#/, "$1♯").replace(/^([A-G])b/, "$1♭");
}

// "ambient, #keys  warm" -> ["ambient", "keys", "warm"].
export function parseTags(text) {
  const out = [];
  for (const t of String(text || "").split(/[,\s]+/)) {
    const tag = t.replace(/#/g, "").trim().toLowerCase();
    if (tag && !out.includes(tag)) out.push(tag);
  }
  return out;
}

// "" -> null; "120" -> 120; out of 20 to 400 or not a number -> NaN.
export function parseBpm(text) {
  const t = String(text ?? "").trim();
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) && n >= 20 && n <= 400 ? n : NaN;
}

// What Edit details changed: { title, patch } (title for POST /loop/name,
// the rest for PATCH /sounds/<h>; null clears a field). An error string
// instead when a field doesn't parse.
export function editChanges(sound, form) {
  const s = sound || {};
  const f = form || {};
  const bpm = parseBpm(f.bpm);
  if (Number.isNaN(bpm)) return { error: "BPM is a number from 20 to 400.", field: "bpm" };
  const title = String(f.title || "").trim();
  if (!title) return { error: "Give the sound a name.", field: "title" };
  const patch = {};
  const oldBpm = s.bpm == null ? null : Math.round(Number(s.bpm) * 100) / 100;
  if ((bpm ?? null) !== oldBpm) patch.bpm = bpm;
  const key = wireKey(f.key);
  if (key !== wireKey(s.key)) patch.key = key;
  const kind = f.kind === "one_shot" ? "one_shot" : "loop";
  if (kind !== (s.kind === "one_shot" ? "one_shot" : "loop")) patch.kind = kind;
  const tags = parseTags(f.tags);
  if (tags.join(",") !== (s.tags || []).join(",")) patch.tags = tags;
  return { title: title !== s.title ? title : null, patch };
}
